/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { ParsedWhatsNewContent, WhatsNewBullet, WhatsNewFeature, WhatsNewRelease } from '../shared/entities/whatsNew';

interface ReleaseWithSortMetadata {
  release: WhatsNewRelease;
  index: number;
  timestamp?: number;
}

interface MarkdownNode {
  type: string;
  depth?: number;
  value?: string;
  children?: MarkdownNode[];
}

// Reuse a single configured processor for predictable parsing behavior.
const parseMarkdownProcessor = unified().use(remarkParse);

// Defensive helper because not every markdown abstract syntax tree (MD AST) node has children.
const getNodeChildren = (node: MarkdownNode): MarkdownNode[] => {
  return Array.isArray(node.children) ? node.children : [];
};

// Ensure markdown formatting artifacts do not leak into rendered copy.
const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

// Flattens node text recursively so emphasis/inline-code/paragraph wrappers become plain text.
const extractNodeText = (node: MarkdownNode): string => {
  if (node.type === 'text' || node.type === 'inlineCode') {
    return node.value ?? '';
  }

  // Preserve explicit markdown line breaks as spaces in output copy.
  if (node.type === 'break') {
    return ' ';
  }

  return getNodeChildren(node)
    .map((childNode: MarkdownNode) => extractNodeText(childNode))
    .join('');
};

const extractHeadingText = (headingNode: MarkdownNode): string => {
  return normalizeWhitespace(extractNodeText(headingNode));
};

// Converts one markdown list item into a bullet, including nested child lists.
const parseListItemNode = (listItemNode: MarkdownNode): WhatsNewBullet => {
  const textSegments: string[] = [];
  const childBullets: WhatsNewBullet[] = [];

  getNodeChildren(listItemNode).forEach((childNode: MarkdownNode) => {
    // Nested <ul>/<ol> become bullet children in our domain model.
    if (childNode.type === 'list') {
      childBullets.push(...parseListNode(childNode));
      return;
    }

    // Non-list children (typically paragraph/text nodes) build the bullet label.
    const childText = normalizeWhitespace(extractNodeText(childNode));
    if (childText) {
      textSegments.push(childText);
    }
  });

  return {
    text: normalizeWhitespace(textSegments.join(' ')),
    children: childBullets,
  };
};

const parseListNode = (listNode: MarkdownNode): WhatsNewBullet[] => {
  return getNodeChildren(listNode)
    .filter((childNode: MarkdownNode) => childNode.type === 'listItem')
    .map((listItemNode: MarkdownNode) => parseListItemNode(listItemNode));
};

const parseReleaseTimestamp = (releaseDate: string): number | undefined => {
  const parsedTimestamp = Date.parse(releaseDate);
  return Number.isNaN(parsedTimestamp) ? undefined : parsedTimestamp;
};

export const parseWhatsNewMarkdown = (markdownContent?: string): ParsedWhatsNewContent => {
  if (!markdownContent || !markdownContent.trim()) {
    return { releases: [] };
  }

  const releases: WhatsNewRelease[] = [];
  let currentRelease: WhatsNewRelease | undefined;
  let currentFeature: WhatsNewFeature | undefined;

  const syntaxTree = parseMarkdownProcessor.parse(markdownContent) as MarkdownNode;

  // Traverse top-level blocks in document order; hierarchy is expressed via heading depth.
  getNodeChildren(syntaxTree).forEach((node: MarkdownNode) => {
    if (node.type === 'heading' && node.depth === 2) {
      // ## Starts a new release section.
      currentRelease = {
        releaseDate: extractHeadingText(node),
        features: [],
      };
      releases.push(currentRelease);
      currentFeature = undefined;
      return;
    }

    if (node.type === 'heading' && node.depth === 3) {
      if (!currentRelease) {
        return;
      }

      // ### Starts a new feature within the current release.
      currentFeature = {
        title: extractHeadingText(node),
        bullets: [],
      };
      currentRelease.features.push(currentFeature);
      return;
    }

    // Lists are only associated to the latest seen feature.
    if (node.type === 'list' && currentFeature) {
      currentFeature.bullets.push(...parseListNode(node));
    }

    // Other markdown blocks (intro copy, images, code fences, etc.) are intentionally ignored.
  });

  // Sort newest-first when dates are parseable, preserving original order for ties/unknown dates.
  const sortedReleases = releases
    .map(
      (release: WhatsNewRelease, index: number): ReleaseWithSortMetadata => ({
        release,
        index,
        timestamp: parseReleaseTimestamp(release.releaseDate),
      }),
    )
    .sort((releaseA: ReleaseWithSortMetadata, releaseB: ReleaseWithSortMetadata) => {
      // Keep original markdown order when no date metadata is available.
      if (releaseA.timestamp === undefined && releaseB.timestamp === undefined) {
        return releaseA.index - releaseB.index;
      }

      // Parseable dates are prioritized over unparseable dates.
      if (releaseA.timestamp === undefined) {
        return 1;
      }

      if (releaseB.timestamp === undefined) {
        return -1;
      }

      if (releaseA.timestamp === releaseB.timestamp) {
        return releaseA.index - releaseB.index;
      }

      return releaseB.timestamp - releaseA.timestamp;
    })
    .map((releaseWithSortMetadata: ReleaseWithSortMetadata) => releaseWithSortMetadata.release);

  return { releases: sortedReleases };
};
